import React, { useState, useEffect } from 'react';
import portalApi from '@/lib/portalApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, FileText, Clock, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_ICONS = {
  REQUESTED: Clock,
  UPLOADED: Upload,
  REVIEWED: Check,
  REJECTED: X,
};

const STATUS_COLORS = {
  REQUESTED: 'bg-blue-100 text-blue-800 border-blue-200',
  UPLOADED: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  REVIEWED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
};

export default function PortalDocumentsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get('/portal/document-requests');
      setRequests(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load document requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const pendingRequests = requests.filter(r => r.status === 'REQUESTED' || r.status === 'REJECTED');
  const completedRequests = requests.filter(r => r.status === 'UPLOADED' || r.status === 'REVIEWED');

  return (
    <div className="space-y-6" data-testid="portal-documents-page">
      <div>
        <h1 className="text-2xl font-bold">My Documents</h1>
        <p className="text-muted-foreground">Upload requested documents</p>
      </div>

      {pendingRequests.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Action Required ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`pending-${req.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{req.documentType} Document</p>
                      <p className="text-sm text-muted-foreground">{req.message || 'Please upload the requested document'}</p>
                      {req.status === 'REJECTED' && req.rejectReason && (
                        <p className="text-sm text-red-600 mt-1">Rejected: {req.rejectReason}</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => { setSelectedRequest(req); setShowUpload(true); }} data-testid={`upload-${req.id}`}>
                    <Upload className="h-4 w-4 mr-2" />Upload
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Document Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No document requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(req => {
                const StatusIcon = STATUS_ICONS[req.status] || Clock;
                return (
                  <div key={req.id} className={`flex items-center justify-between p-4 rounded-lg border ${STATUS_COLORS[req.status]}`} data-testid={`request-${req.id}`}>
                    <div className="flex items-center gap-3">
                      <StatusIcon className="h-5 w-5" />
                      <div>
                        <p className="font-medium">{req.documentType}</p>
                        <p className="text-sm opacity-80">{req.participantName}</p>
                        <p className="text-xs opacity-60">{req.createdAt?.slice(0, 10)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium">{req.status}</span>
                      {req.status === 'REQUESTED' && (
                        <Button size="sm" variant="outline" className="ml-3" onClick={() => { setSelectedRequest(req); setShowUpload(true); }}>
                          Upload
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document - {selectedRequest?.documentType}</DialogTitle></DialogHeader>
          {selectedRequest && (
            <UploadForm
              requestId={selectedRequest.id}
              onSuccess={() => { setShowUpload(false); setSelectedRequest(null); fetchRequests(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadForm({ requestId, onSuccess }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (notes) formData.append('notes', notes);
      await portalApi.post(`/portal/document-requests/${requestId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Select File *</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional information..." rows={3} />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Uploading...' : 'Upload Document'}</Button>
    </form>
  );
}
